import React from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  CircularProgress
} from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import "./AddCorporateUserComponent.css";

const AddCorporateUserComponent = (props) => {
  const {
    singleUser,
    setSingleUser,
    handleCreateNewUser,
    openBulkModal,
    setOpenBulkModal,
    handleBulkFileUpload,
    parsedBulkUsers,
    handleBulkAddUser,
    downloadCSVTemplate,
    setParsedBulkUsers,
    bulkFile,
    setBulkFile,
    departments,
    departmentOptions,
    isLoading,
    isEditMode,
    corporateId,
    userHasDepartment,
    existingDepartment,
    onlyDefaultDepartment
  } = props;

  // Handle file selection
  const onFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
        alert("Please upload a CSV file");
        return;
      }
      handleBulkFileUpload(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
        alert("Please upload a CSV file");
        return;
      }
      handleBulkFileUpload(file);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setBulkFile(null);
    setParsedBulkUsers([]);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSingleUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle department change
  const handleDepartmentChange = (e) => {
    const selectedDeptId = e.target.value;
    const selectedDept = departments.find(d => d.id.toString() === selectedDeptId);
    
    setSingleUser(prev => ({
      ...prev,
      departmentId: selectedDeptId,
      departmentName: selectedDept ? selectedDept.name : ""
    }));
  };

  return (
    <div className="corporate-user-management-container">
      <Sidebar />
      
      <div className="add-corporate-user-main-content">
        {/* Header */}
        <div className="add-corporate-user-header">
          <ArrowBackIosIcon 
            className="back-arrow" 
            onClick={() => window.history.back()}
          />
          <Typography variant="h5" className="add-corporate-user-title">
            {isEditMode ? 'Edit Corporate User' : 'Add Corporate User'}
          </Typography>
          <button
            className="bulk-user-button"
            onClick={() => setOpenBulkModal(true)}
            disabled={isLoading}
          >
            + Bulk Add User
          </button>
        </div>

        {/* Form Box */}
        <Box className="add-corporate-user-form-box">
          <div className="add-corporate-user-form">
            {/* Employee Name */}
            <div className="form-group">
              <label>
                Employee Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="employeeName"
                value={singleUser.employeeName}
                onChange={handleInputChange}
                placeholder="Enter employee name"
                disabled={isLoading}
                required
              />
            </div>

            {/* Employee ID */}
            <div className="form-group">
              <label>
                Employee ID <span className="required">*</span>
              </label>
              <input
                type="text"
                name="employeeId"
                value={singleUser.employeeId}
                onChange={handleInputChange}
                placeholder="Enter employee ID"
                disabled={isLoading || isEditMode}
                required
              />
            </div>

            {/* Employee Email */}
            <div className="form-group">
              <label>
                Employee Email <span className="required">*</span>
              </label>
              <input
                type="email"
                name="employeeEmail"
                value={singleUser.employeeEmail}
                onChange={handleInputChange}
                placeholder="Enter employee email"
                disabled={isLoading || isEditMode}
                required
              />
            </div>

            {/* Department Selection */}
            <div className="form-group">
              <label>
                Department <span className="required">*</span>
              </label>
              {userHasDepartment && !onlyDefaultDepartment ? (
                // Show disabled field if user already has a department
                <div className="department-field-container">
                  <input
                    type="text"
                    value={existingDepartment}
                    disabled
                    className="readonly-field"
                  />
                  <div className="department-disabled-info">
                    <small style={{ color: '#666' }}>
                      Department cannot be changed as user already has an assigned department
                    </small>
                  </div>
                </div>
              ) : (
                // Show select dropdown
                <select
                  name="departmentId"
                  value={singleUser.departmentId || ""}
                  onChange={handleDepartmentChange}
                  className={`department-select ${onlyDefaultDepartment ? 'disabled-department' : ''}`}
                  disabled={isLoading || onlyDefaultDepartment}
                  required
                >
                  <option value="">Select Department</option>
                  {departmentOptions.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              )}
              {onlyDefaultDepartment && (
                <div className="department-disabled-info">
                  <small style={{ color: '#f59e0b' }}>
                    Only default department available. Contact admin to add more departments.
                  </small>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              className="submit-button"
              onClick={handleCreateNewUser}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update User' : 'Create User'
              )}
            </button>
          </div>
        </Box>
      </div>

      {/* Bulk Upload Modal */}
      <Modal
        open={openBulkModal}
        onClose={() => !isLoading && setOpenBulkModal(false)}
      >
        <div className="bulk-upload-modal">
          <h2>Bulk Upload Users</h2>
          <p>Upload a CSV file with user details. Download the template below for the correct format.</p>

          {/* Template Download Section */}
          <div className="template-section">
            <h3>CSV Format Template</h3>
          
            <button 
              className="download-template-btn"
              onClick={downloadCSVTemplate}
            >
              <DownloadIcon /> Download Template
            </button>
          </div>

          {/* File Upload Area - FIXED */}
          <div className="file-upload-area">
            <div 
              className={`drop-zone ${bulkFile ? 'active' : ''}`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                className="file-input"
                accept=".csv"
                onChange={onFileSelect}
                disabled={isLoading}
              />
              <CloudUploadIcon className="upload-icon" />
              {bulkFile ? (
                <div className="file-info">
                  <strong>{bulkFile.name}</strong>
                 
                </div>
              ) : (
                <>
                  <div>
                    <span className="choose-file">Choose a file</span> or drag it here
                  </div>
                  <div className="drop-text">
                    Supported format: CSV (max size: 5MB)
                  </div>
                </>
              )}
            </div>
          <div style={{marginTop:"10px"}}>
             <button 
                    className="remove-file-btn"
                    onClick={handleRemoveFile}
                    disabled={isLoading}
                  >
                    <DeleteIcon fontSize="small" /> Remove
                  </button>
          </div>
            
          </div>

        
          {/* Modal Actions */}
          <div className="modal-actions">
            <button
              className="cancel-btn"
              onClick={() => {
                setOpenBulkModal(false);
                handleRemoveFile();
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="upload-btn"
              onClick={handleBulkAddUser}
              disabled={parsedBulkUsers.length === 0 || isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Uploading...
                </>
              ) : (
                <>
                  <CloudUploadIcon /> Upload {parsedBulkUsers.length} Users
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddCorporateUserComponent;